import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import "./WriteDivField.css";

const WriteDivField = forwardRef(
  (
    {
      style,
      parentClassName,
      className,
      handleInput,
      handleBlur,
      placeholder,
      placeholderRight,
      disabled,
      onFocus,
      value = "",
      sanitize,
      showClearIcon = true,
      onClear,
      ...rest
    },
    ref
  ) => {
    const divRef = useRef(null);

    useImperativeHandle(ref, () => divRef.current);

    useEffect(() => {
      if (
        divRef.current &&
        document.activeElement !== divRef.current &&
        divRef.current.innerText !== value
      ) {
        divRef.current.innerText = value;
      }
    }, [value]);

    const handleClearContent = (e) => {
      e.stopPropagation();
      if (divRef.current) {
        divRef.current.innerText = "";
        handleInput("");
        if (onClear) {
          onClear();
        }
      }
    };

    const sanitizeContent = (content) => {
      return content.replace(/[^a-zA-Z\u0900-\u097F\u0A80-\u0AFF0-9\s]/g, "");
    };

    const handleInputChange = () => {
      if (divRef.current) {
        const rawText = divRef.current.textContent;
        const sanitizedText = sanitize
          ? sanitize(rawText)
          : sanitizeContent(rawText);
        handleInput(sanitizedText);
        if (divRef.current.innerText !== sanitizedText) {
          divRef.current.innerText = sanitizedText;
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(divRef.current);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const range = document.createRange();
      const sel = window.getSelection();
      const lastChild = e.target.lastChild;
      if (lastChild) {
        range.setStartAfter(lastChild);
        range.collapse(true);
      } else {
        range.setStart(e.target, 0);
        range.collapse(true);
      }
      sel.removeAllRanges();
      sel.addRange(range);
    };

    const handleTouchEnd = (e) => {
      e.target.focus();
    };

    const handleBlurEvent = () => {
      if (divRef.current && divRef.current.innerText.trim() !== "") {
        if (handleBlur) {
          handleBlur();
        }
      }
    };

    return (
      <div
        className={`${parentClassName} bg-white flex items-end`}
        style={{ ...style }}
        {...rest}
      >
        <div
          ref={divRef}
          className={`${className} ${disabled ? "disabled" : ""}`}
          contentEditable="true"
          onInput={handleInputChange}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onBlur={handleBlurEvent}
          onFocus={onFocus}
          data-placeholder={placeholder}
          data-placeholder-right={placeholderRight}
          suppressContentEditableWarning={true}
        ></div>
        {showClearIcon && (
          <span
            onClick={handleClearContent}
            className="material-symbols-outlined p-0 w-auto font-semibold text-sm"
          >
            format_clear
          </span>
        )}
      </div>
    );
  }
);

export default WriteDivField;
